<template>
    <div style="display: flex; align-items: flex-end; gap: 18px; flex-wrap: wrap;">
        <div class="stl-field">
            <label>총요금 (원)</label>
            <AmountKinput
                :value="amount"
                :disabled="disabled || source === 'KEPCO'"
                @input="setAmount"
                @enter="$emit('calculate')"
            />
        </div>

        <div class="stl-radio-group" style="padding-bottom: 10px;">
            <label>
                <input type="radio" value="MANUAL" :checked="source === 'MANUAL'" :disabled="disabled" @change="setSource('MANUAL')" />
                수동 입력
            </label>
            <label :title="kepcoUnavailableReason || ''">
                <input
                    type="radio"
                    value="KEPCO"
                    :checked="source === 'KEPCO'"
                    :disabled="disabled || !kepcoUsable"
                    @change="setSource('KEPCO')"
                />
                한전 불러오기
                <span v-if="!kepcoUsable" class="stl-hint">(연동 미설정)</span>
            </label>
        </div>

        <div v-if="source === 'KEPCO'" style="display: flex; align-items: center; gap: 8px; padding-bottom: 4px; flex-wrap: wrap;">
            <button type="button" class="stl-btn stl-btn-sm" :disabled="disabled || loadingBill" @click="loadKepcoBill">
                <b-spinner v-if="loadingBill" small />
                <i v-else class="fa fa-cloud-download"></i>
                고지 조회
            </button>
            <template v-if="kepcoBill">
                <span style="font-size: 12px;">
                    청구액 <strong class="num">{{ fmtWon(kepcoBill.reqAmt) }}원</strong>
                    <span v-if="kepcoBill.totalKwh" class="stl-hint">/ {{ fmtKwh(kepcoBill.totalKwh) }} kWh</span>
                </span>
                <button type="button" class="stl-btn stl-btn-tint stl-btn-sm" :disabled="disabled" @click="applyKepco">적용</button>
            </template>
            <span v-else-if="kepcoError" class="stl-hint" style="color: #c13a33;">{{ kepcoError }}</span>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import AmountKinput from "./AmountKinput.vue";
import { fmtWon, fmtKwh, parseApiError } from "../utils/settlementFormat.js";

export default {
    name: "KepcoBillPanel",
    components: { AmountKinput },
    props: {
        site: { type: Object, required: true },
        regYm: { type: String, required: true },
        value: { type: Object, default: () => ({ kepcoSource: "MANUAL", kepcoTotalAmount: null }) },
        disabled: { type: Boolean, default: false },
    },
    data() {
        return {
            loadingBill: false,
            kepcoBill: null,
            kepcoError: null,
        };
    },
    computed: {
        source() {
            return this.value?.kepcoSource || "MANUAL";
        },
        amount() {
            return this.value?.kepcoTotalAmount ?? null;
        },
        kepcoUsable() {
            return this.site?.kepcoEnabled === "Y" && !!this.site?.kepcoCustNo;
        },
        effectiveCustNo() {
            return this.site?.kepcoCustNo;
        },
        kepcoUnavailableReason() {
            if (this.kepcoUsable) return null;
            return "단지정보에서 한전 연동을 먼저 구성하세요.";
        },
    },
    watch: {
        regYm() {
            this.kepcoBill = null;
            this.kepcoError = null;
        },
    },
    methods: {
        fmtWon,
        fmtKwh,
        emitValue(patch) {
            this.$emit("input", { kepcoSource: this.source, kepcoTotalAmount: this.value?.kepcoTotalAmount ?? null, ...patch });
        },
        setSource(src) {
            this.kepcoError = null;
            this.emitValue({ kepcoSource: src, kepcoTotalAmount: null });
        },
        setAmount(v) {
            this.emitValue({ kepcoTotalAmount: v });
        },
        async loadKepcoBill() {
            this.loadingBill = true;
            this.kepcoBill = null;
            this.kepcoError = null;
            try {
                const { data } = await backEndApi.settlement.getKepcoBill({
                    custNo: this.effectiveCustNo,
                    regYm: this.regYm,
                });
                this.kepcoBill = data;
            } catch (err) {
                const e = parseApiError(err);
                this.kepcoError =
                    e.errcode === "KEPCO_BILL_NOT_FOUND"
                        ? "해당 월 고지 없음 — 수동 입력을 이용하세요."
                        : e.message;
            } finally {
                this.loadingBill = false;
            }
        },
        applyKepco() {
            if (!this.kepcoBill) return;
            this.emitValue({ kepcoSource: "KEPCO", kepcoTotalAmount: Math.trunc(this.kepcoBill.reqAmt) });
            this.$nextTick(() => this.$emit("calculate"));
        },
    },
};
</script>
